// App.js
import React, { useEffect } from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { Switch, Redirect, Route } from "react-router-dom";
import { overrideConsole } from "./utils/loggingErrors";
import { GridLoadingSpinner, Notification } from "./components";
import PrivateRoute from "./routing/PrivateRoute";
import ThemeProvider from "./theme";
import ApiBasedInactivityCheck from "./apis/ApiBasedInactivityCheck";
import Dashboard from "./pages/Dashboard/Dashboard";
import Layout from "./components/Layout/Layout";
import IEPFReport from "./pages/IEPFReport/IEPFReport";
import {
  ProtectedApproverApproved,
  ProtectedCompany,
  ProtectedELProcess,
  ProtectedEVerification,
  ProtectedApproverPending,
  ProtectedPerformerApproved,
  ProtectedQuestionnaire,
  ProtectedApproverRejected,
  ProtectedShareholder,
  ProtectedSRN,
  ProtectedPerformerPending,
  ProtectedPerformerRejected,
  ProtectedPendingSRN,
  ProtectedPerformerInApproval,
  ProtectedTicketStatus,
  ProtectedCloseTicket,
  ProtectedRTARectify,
} from "./pages";
import { ROUTES } from "./constants";
import CustomSnackbar from "./components/CustomSnackbar/CustomSnackbar";
import { hideSnackbar } from "./redux/features/customSnackbar/action";
import { usePopupManager } from "./providers/PopupManager/PopupManager";
import { setGlobalShowError } from "./apis/api";
import NotFound from "./pages/NotFound/NotFound";

function App({ isAuthenticated, user, isLoading }) {
  const dispatch = useDispatch();
  const { showError } = usePopupManager();

  const addLoggingHooks = () => {
    window.onerror = (message, source, lineno, colno, error) => {
      if (error.stack) {
        const sourceFunction = error.stack.split("\n")[1];
        console.error(new Error(message + sourceFunction));
      } else {
        console.error(error);
      }
    };
    const newConsole = overrideConsole(window.console, user?.userId);
    window.console = newConsole;
  };

  const snackbarState = useSelector((state) => state.customSnackbar);

  const handleCloseSnackbar = () => {
    dispatch(hideSnackbar());
  };

  useEffect(() => {
    if (isAuthenticated) {
      addLoggingHooks();
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    setGlobalShowError(showError);
  }, [showError]);

  return (
    <>
      <div data-testid="app" className="App">
        {isLoading ? (
          <div
            style={{
              margin: "40vh auto",
              height: "20vh",
              width: "20vh",
              textAlign: "center",
            }}
          >
            <GridLoadingSpinner />
          </div>
        ) : (
          <ThemeProvider>
            <Switch>
              <Layout>
                <Switch>
                {/* <Header /> */}
                <Route
                  exact
                  path="/"
                  render={() =>
                    user?.isRTA ? (
                      <Redirect to={ROUTES.IEPFReport} />
                    ) : (
                      <Redirect to={ROUTES.DASHBOARD} />
                    )
                  }
                />

                <PrivateRoute
                  exact
                  path={ROUTES.DASHBOARD}
                  component={Dashboard}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.IEPFReport}
                  component={IEPFReport}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.TICKET_STATUS}
                  component={ProtectedTicketStatus}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.RTA_RECTIFY}
                  component={ProtectedRTARectify}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.IEPFReportDetails}
                  component={IEPFReport}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.QUESTIONNAIRE}
                  component={ProtectedQuestionnaire}
                />

                {/* Approver Routes */}
                <PrivateRoute
                  exact
                  path={ROUTES.APPROVER_APPROVED}
                  component={ProtectedApproverApproved}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.PERFORMER_IN_APPROVAL}
                  component={ProtectedPerformerInApproval}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.APPROVER_PENDING}
                  component={ProtectedApproverPending}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.APPROVER_CLOSE}
                  component={ProtectedCloseTicket}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.APPROVER_REJECTED}
                  component={ProtectedApproverRejected}
                />

                {/* Performer Routes */}
                <PrivateRoute
                  exact
                  path={ROUTES.PERFORMER_APPROVED}
                  component={ProtectedPerformerApproved}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.PERFORMER_PENDING}
                  component={ProtectedPerformerPending}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.PERFORMER_REJECTED}
                  component={ProtectedPerformerRejected}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.PERFORMER_COMPANY}
                  component={ProtectedCompany}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.PERFORMER_SHAREHOLDER}
                  component={ProtectedShareholder}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.PERFORMER_EL_PROCESS}
                  component={ProtectedELProcess}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.PERFORMER_E_VERIFICATION}
                  component={ProtectedEVerification}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.PERFORMER_SRN}
                  component={ProtectedSRN}
                />

                <PrivateRoute
                  exact
                  path={ROUTES.PERFORMER_PENDING_SRN}
                  component={ProtectedPendingSRN}
                />

                {/* Explicit 404 route for programmatic redirects */}
                <Route exact path="/404" component={NotFound} />

                {/* 404 Fallback */}
                <Route component={NotFound} />
                </Switch>

                <Notification />
                <CustomSnackbar
                  {...snackbarState}
                  onClose={handleCloseSnackbar}
                />
              </Layout>
            </Switch>

            <ApiBasedInactivityCheck />
          </ThemeProvider>
        )}
      </div>
    </>
  );
}

App.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  user: PropTypes.object,
  isLoading: PropTypes.bool.isRequired,
};

export default connect(({ auth }) => ({
  isAuthenticated: auth.isAuthenticated,
  user: auth.user,
  isLoading: auth.isLoading,
}))(App);
